import Alert from '@mui/material/Alert';
import { BigNumber, utils } from 'ethers';
import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useAccount, useBlockNumber } from 'wagmi';
import { ContractContext } from '../../../context-providers/ContractsProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { contractAddresses, tokenDecimals } from '../../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../../helpers/error-message.helper';
import { stakeBalanceStore } from '../../../stores/StakeBalanceStore';
import AmountInput from '../../AmountInput/AmountInput';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import './StakeDialog.scss';
import {balanceStore} from "../../../stores/BalanceStore";

const buttonTexts = {
    approveAndStake: 'Approve and Stake NRS',
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds',
    approving: 'Approving NRS',
    deposit: 'Stake',
    depositing: 'Staking',
    retryApproveAndStake: 'Retry Approve and Stake NRS',
    retryStake: 'Retry Stake NRS'
};

const secondsInYear = 365n * 24n * 60n * 60n

const StakeDialog: React.FC = observer(() => {
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { depositWithdrawStakeService } =  useContext(ServicesContext);
    const [amount, setAmount] = useState<string>('0');
    const [error, setError] = useState<string | null>(null);
    const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
    const [approvedAmount, setApprovedAmount] = useState<BigNumber>(BigNumber.from('0'));
    const [buttonText, setButtonText] = useState<string>(buttonTexts.enterAmount);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isRetry, setIsRetry] = useState<boolean>(false);
    const { nrsContract, veNrsContract, nrsRewardControllerContract } = useContext(ContractContext);
    const [apr, setApr] = useState('')

    useEffect(() => {
        const getAndSetApprovedAmount = async () => {
            const allowed = await nrsContract.read.allowance([userAddress, contractAddresses.polygon.veNrs]);

            setApprovedAmount(BigNumber.from(allowed));
        }
        if (userAddress && blockNumber && nrsContract) {
            getAndSetApprovedAmount();
        } else {
            setApprovedAmount(BigNumber.from('0'));
        }

    }, [userAddress, blockNumber, nrsContract]);

    useEffect(() => {
        if (!amount || Number(amount) === 0) {
            return setButtonText(buttonTexts.enterAmount);
        } else {
            if (error) {
                if (error === hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE) {
                    return setButtonText(buttonTexts.insufficientFunds);
                }
            }
            if (approvedAmount.gte(utils.parseUnits(amount, tokenDecimals.nrs))) {
                if (isPending) {
                    return setButtonText(buttonTexts.depositing);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryStake : buttonTexts.deposit);
                }

            } else {
                if (isPending) {
                    return setButtonText(buttonTexts.approving);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryApproveAndStake : buttonTexts.approveAndStake);
                }
            }
        }

    }, [amount, error, approvedAmount, isRetry, isPending]);

    useEffect(() => {
        if ((amount && Number(amount) > 0) && (error !== hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE)) {
            setBtnDisabled(false);
        } else {
            setBtnDisabled(true);
        }
    }, [amount, error]);

    useEffect(() => {
        if (amount && Number(amount) > Number(stakeBalanceStore.nrsTokenBalanceDecimal)) {
            setError(hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE);
        } else {
            setError(null);
        }
    }, [amount, stakeBalanceStore.nrsTokenBalanceDecimal]);

    useEffect(() => {
        async function fetch() {
            try {
                if (userAddress && nrsRewardControllerContract && veNrsContract) {
                    const rewardsRate = await nrsRewardControllerContract.read.rewardsRate()
                    const rewardsPeriod = await nrsRewardControllerContract.read.rewardsPeriod()
                    const totalAssets = await veNrsContract.read.totalAssets()

                    if (totalAssets > 0) {
                        const precision = 100n
                        const apr = secondsInYear * rewardsRate * 100n * precision / rewardsPeriod / totalAssets
                        setApr(`${Number((Number(apr) / Number(precision)).toFixed(2))}%`)
                    }

                }
            } catch (err) {
                console.error(err)
            }
        }
        fetch()
    }, [userAddress, veNrsContract, nrsRewardControllerContract, stakeBalanceStore.nrsAvailableBalance]);


    const handleApproveAndStake = async () => {
        setIsPending(true);
        setIsRetry(false);
        if (approvedAmount.lt(utils.parseUnits(amount, tokenDecimals.nrs))) {
            try {
                setButtonText(buttonTexts.approving);
                await depositWithdrawStakeService.approveAmount(utils.parseUnits(amount, tokenDecimals.nrs));
            } catch (err) {
                setIsPending(false);
                setIsRetry(true);
                return;
            }
        }

        try {
            setButtonText(buttonTexts.depositing);
            await depositWithdrawStakeService.deposit(utils.parseUnits(amount, tokenDecimals.nrs));
            if (userAddress) {
                balanceStore.fetchBalance(userAddress)
            }
        } catch (err) {
            setButtonText(buttonTexts.retryStake);
            setIsPending(false);
            setIsRetry(true);
            return;
        }
        setIsPending(false);
    };

    return (
        <ModalWrapper className="StakeDialog"
            headerChildren={<h2 className="nereus-body-m">Stake</h2>}
            bodyChildren={<div className="StakeDialog__body">
                <div>
                    <AmountInput label="Amount" asset="NRS" hasMaxButton={true} value={amount}
                                 available={stakeBalanceStore.nrsTokenBalanceDecimal}
                                 onChange={amount => setAmount(amount + '')}/>
                    {error && <Alert className="StakeDialog__error mt-4" variant="standard" severity="error">
                        <span className="nereus-caption">{error}</span>
                    </Alert>}
                    <div className="StakeDialog__apr flex flex-row justify-between">
                        <div>Current APR</div>
                        <div className="value">{apr}</div>
                    </div>
                    <Alert className="StakeDialog__error mt-4" variant="standard" severity="info">
                        <span className="nereus-caption"><span className="nereus-color-main">Stake NRS and start earning rewards instantly with no limits.</span> Withdrawals require a 30-day redemption period to ensure stability and maximize community rewards.</span>
                    </Alert>
                </div>

                <div className="flex pb-4 mt-4">
                    <NereusButton disabled={btnDisabled || isPending} variant="contained"
                                  onClick={handleApproveAndStake} className="bg-light-green text-dark-blue w-full">
                        {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending"/>}
                    </NereusButton>
                </div>
            </div>}
        />
    );
});

export default StakeDialog;
